import { ReactNode } from 'react'
import { PiArrowRight, PiEye } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'

/** Number of sample values shown in the preview popover. */
const MAX_PREVIEW_SAMPLES = 6

interface SampleDataPreviewProps {
  /** Original CSV header, used both as the title pill and the row lookup key. */
  csvHeader: string
  /** Mapped First Bite column label, or undefined when skipped/unmapped. */
  mappedLabel?: string
  /** True when this column will not be imported (excluded or skipped). */
  isNotIncluded: boolean
  /** Whole-CSV rows, keyed by the ORIGINAL CSV header. */
  rows: Record<string, unknown>[]
  /** Controls the active-preview accent on the trigger. */
  isActive: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Stringify a cell value and drop empty/blank entries. */
function nonEmptySamples(
  rows: Record<string, unknown>[],
  key: string
): string[] {
  const out: string[] = []
  for (const row of rows) {
    const raw = row[key]
    if (raw === null || raw === undefined) continue
    const str = String(raw).trim()
    if (str === '') continue
    out.push(str)
    if (out.length >= MAX_PREVIEW_SAMPLES) break
  }
  return out
}

/** Count of non-empty values across the whole CSV for the footer line. */
function nonEmptyCount(rows: Record<string, unknown>[], key: string): number {
  let count = 0
  for (const row of rows) {
    const raw = row[key]
    if (raw === null || raw === undefined) continue
    if (String(raw).trim() === '') continue
    count += 1
  }
  return count
}

/**
 * Eye button + popover showing real sample data pulled from the uploaded CSV
 * for a single column. Samples come from `rows` keyed by the original header.
 * Only one preview is open at a time (controlled via open/onOpenChange).
 */
export function SampleDataPreview({
  csvHeader,
  mappedLabel,
  isNotIncluded,
  rows,
  isActive,
  open,
  onOpenChange,
}: SampleDataPreviewProps) {
  const samples = nonEmptySamples(rows, csvHeader)
  const total = nonEmptyCount(rows, csvHeader)

  let body: ReactNode
  if (samples.length > 0) {
    body = (
      <div className="flex flex-col gap-1.5">
        {samples.map((value, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-surface-sunken',
              isNotIncluded && 'opacity-60'
            )}
          >
            <span className="size-1.5 rounded-full bg-fg-muted shrink-0" />
            <span
              className={cn(
                'text-xs font-mono truncate',
                isNotIncluded ? 'text-fg-muted' : 'text-fg-secondary'
              )}
              title={value}
            >
              {value}
            </span>
          </div>
        ))}
        <p className="text-xs text-fg-muted pt-1 text-right">
          {total} row{total !== 1 ? 's' : ''} from your CSV
        </p>
      </div>
    )
  } else {
    body = (
      <p className="text-xs text-fg-muted italic">No sample data available</p>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Preview sample data"
          aria-label={`Preview sample data for "${csvHeader}"`}
          className={cn(
            'size-7 rounded-md flex items-center justify-center transition-all',
            isActive
              ? 'bg-success-bg text-success'
              : 'text-fg-muted hover:text-brand hover:bg-surface-hover'
          )}
        >
          <PiEye className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-line bg-surface-sunken flex flex-col gap-1">
          <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
            Sample Data
          </span>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-mono bg-surface px-1.5 py-px rounded text-fg-secondary truncate">
              {csvHeader}
            </span>
            {mappedLabel !== undefined && (
              <>
                <PiArrowRight className="size-3 text-fg-muted shrink-0" />
                <span className="text-xs text-fg-secondary truncate">
                  {mappedLabel}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="px-4 py-3">{body}</div>
        {isNotIncluded && (
          <div className="px-4 pb-3 -mt-1">
            <p className="text-xs text-fg-muted font-medium">
              This column will not be imported
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
